<script setup>
import { computed, useSlots, useTemplateRef } from 'vue';
import { useValidation, useFormContext } from '/app/composables/form';
import InputMasked from './InputMasked.vue';
import { vElementVisibility as vVisible } from '@vueuse/components';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    name: { type: String, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    fieldName: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    forceError: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    autocomplete: { type: String, default: null },
    modelValue: {
        type: [String, Number],
        default: ''
    },
    type: {
        type: String,
        default: 'text'
    },
    mask: {
        type: [String, Array],
    },
});

const emit = defineEmits(['update:modelValue']);

const form = useFormContext();
const { invalid, error, attrs, clearErrors, setError, focus, target } = useValidation(props);
const slots = useSlots();
const targetRef = useTemplateRef('target');

const hasPrepend = computed(() => !!slots['prepend']);
const hasAppend = computed(() => !!slots['append']);

function listeners({ ignore = [] } = {}) {
    return {};
}

function update(value) {
    clearErrors();
    emit('update:modelValue', value);
}

function tryFocus(visible, focusFlag) {
    if (!visible || !focusFlag) {
        return;
    }

    setTimeout(() => {
        targetRef.value?.$el?.focus();
    }, 100);
}

function focusInput() {
    const el = targetRef.value?.$el;
    if (el && typeof el.focus === 'function') {
        el.focus();
    }
}

defineExpose({ focus: focusInput });
</script>

<template>
    <div v-visible="(e) => tryFocus(e, autofocus)" class="form-group__helper">
        <label v-if="label" :for="name" class="form-group__label" style="display: block;">{{ label }}</label>
        <span v-if="hint" class="form-group__hint" style="display: block;">{{ hint }}</span>
        <div class="form-group__input-helper ">
            <div class="form-group__input !w-full !flex items-center border border-dust-300 rounded-md" :class="{ 'border-red-400': invalid, 'bg-dust-100': disabled }">
                <span v-if="hasPrepend">
                    <slot name="prepend"></slot>
                </span>
                <InputMasked
                    v-bind="attrs"
                    v-on="(listeners({ ignore: ['update:modelValue'] }))"
                    ref="target"
                    :type="type"
                    :id="name"
                    :name="name"
                    :modelValue="modelValue"
                    :disabled="form.busy || disabled"
                    :options="{ mask }"
                    :autofocus="autofocus"
                    :autocomplete="autocomplete"
                    class="w-full focus:placeholder-transparent bg-transparent"
                    @update:modelValue="update"
                />
                <span v-if="hasAppend" class="form-group__append">
                    <slot name="append" />
                </span>
            </div>
        </div>
        <span v-if="invalid" class="form-group__error">{{ error }}</span>

        <slot :error="error" :disabled="disabled" :invalid="invalid"  />
    </div>
</template>
